import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { DISPATCH_EVENT_KEYS } from 'src/consumer/constants'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import { CheckInIdlePopup } from 'src/mentor/components/MentorEventsPage/components/CheckInIdlePopup'
import { LockIconButton } from 'src/mentor/components/MentorEventsPage/components/LockIconButton'
import { getActivePeekIns } from 'src/mentor/components/MentorEventsPage/utils/peekInManager'
import {
  EMR_CHAMBER_AUTO_CONNECT_PAUSE_EVENT,
  EMR_VIDEO_CALL_ACTIVITY_EVENT,
  isEmrChamberAutoConnectRoutePath,
  isEmrCheckInZonePath,
  isEmrVideoCallRoutePath,
  readEmrChamberAutoConnectPause,
  readEmrVideoCallActivity,
} from './checkInZoneRoutes'

function readJsonStorageItem(key) {
  try {
    const storedValue = localStorage.getItem(key)
    return storedValue ? JSON.parse(storedValue) : null
  } catch {
    return null
  }
}

function buildClinicLocationData(checkedInDetails, peekIns) {
  const peekIn = peekIns?.[0]
  const clinicLocationId = checkedInDetails?.clinicLocationId || peekIn?.clinicLocationId

  if (!clinicLocationId) {
    return null
  }

  const clinicName =
    checkedInDetails?.clinicLocationName ||
    checkedInDetails?.clinicName ||
    checkedInDetails?.locationName ||
    checkedInDetails?.clinicLocationTitle ||
    peekIn?.clinicLocationName ||
    peekIn?.clinicName ||
    peekIn?.locationName ||
    peekIn?.title ||
    'Clinic'

  return {
    id: clinicLocationId,
    name: clinicName,
    title: clinicName,
    timeZone:
      checkedInDetails?.clinicLocationTimeZone ||
      checkedInDetails?.timeZone ||
      peekIn?.clinicLocationTimeZone ||
      peekIn?.timeZone,
    schedules: checkedInDetails?.schedules || peekIn?.schedules,
    clinicBrandName:
      checkedInDetails?.clinicBrandName || checkedInDetails?.brandName || peekIn?.clinicBrandName,
    clinicBrandData:
      checkedInDetails?.clinicBrandData || peekIn?.clinicBrandData || null,
    clinicBrandIcon:
      checkedInDetails?.clinicBrandIcon ||
      checkedInDetails?.clinicBrandIconImage ||
      checkedInDetails?.icon ||
      peekIn?.clinicBrandIcon ||
      peekIn?.clinicBrandIconImage ||
      peekIn?.icon,
    clinicBrandCoverIcon:
      checkedInDetails?.clinicBrandCoverIcon ||
      checkedInDetails?.coverIcon ||
      peekIn?.clinicBrandCoverIcon ||
      peekIn?.coverIcon,
    clinicBrandCoverImage:
      checkedInDetails?.clinicBrandCoverImage ||
      checkedInDetails?.clinicBrandImage ||
      checkedInDetails?.coverImage ||
      peekIn?.clinicBrandCoverImage ||
      peekIn?.clinicBrandImage ||
      peekIn?.coverImage,
  }
}

function readIdleSnapshot() {
  const checkedInDetails = readJsonStorageItem('checkedInDetails')
  const peekIns = getActivePeekIns()

  return {
    checkedInDetails,
    peekIns,
    clinicLocationData: buildClinicLocationData(checkedInDetails, peekIns),
    chamberAutoConnectPauseActive: readEmrChamberAutoConnectPause(),
    videoCallActive: readEmrVideoCallActivity(),
  }
}

function isPractitionerUser() {
  const roles = getAllRolesInBoolean()
  return Boolean(roles?.isDoctor || roles?.isTherapist || roles?.isScreener)
}

export default function EmrCheckInIdleManager() {
  const location = useLocation()
  const [snapshot, setSnapshot] = useState(readIdleSnapshot)
  const [manualIdleTrigger, setManualIdleTrigger] = useState(false)

  const isInEmrZone = isEmrCheckInZonePath(location.pathname)
  const isChamberAutoConnectRoute = isEmrChamberAutoConnectRoutePath(location.pathname)
  const isVideoCallSurface =
    isEmrVideoCallRoutePath(location.pathname) || snapshot.videoCallActive
  const isChamberAutoConnectPauseActive =
    isChamberAutoConnectRoute && snapshot.chamberAutoConnectPauseActive
  const hasEmrRoomAccess = Boolean(snapshot.checkedInDetails || snapshot.peekIns.length > 0)

  const shouldEnableIdleManager = useMemo(
    () =>
      isPractitionerUser() &&
      isInEmrZone &&
      hasEmrRoomAccess &&
      !isVideoCallSurface &&
      !isChamberAutoConnectPauseActive,
    [hasEmrRoomAccess, isChamberAutoConnectPauseActive, isInEmrZone, isVideoCallSurface],
  )

  useEffect(() => {
    const refreshSnapshot = () => {
      setSnapshot(readIdleSnapshot())
    }

    const intervalId = setInterval(refreshSnapshot, 10000)

    window.addEventListener('storage', refreshSnapshot)
    window.addEventListener(EMR_CHAMBER_AUTO_CONNECT_PAUSE_EVENT, refreshSnapshot)
    window.addEventListener(EMR_VIDEO_CALL_ACTIVITY_EVENT, refreshSnapshot)
    window.addEventListener(DISPATCH_EVENT_KEYS.userCheckedIn, refreshSnapshot)
    window.addEventListener(DISPATCH_EVENT_KEYS.userRefreshCheckedIn, refreshSnapshot)
    window.addEventListener(DISPATCH_EVENT_KEYS.userCheckedOut, refreshSnapshot)

    return () => {
      clearInterval(intervalId)
      window.removeEventListener('storage', refreshSnapshot)
      window.removeEventListener(EMR_CHAMBER_AUTO_CONNECT_PAUSE_EVENT, refreshSnapshot)
      window.removeEventListener(EMR_VIDEO_CALL_ACTIVITY_EVENT, refreshSnapshot)
      window.removeEventListener(DISPATCH_EVENT_KEYS.userCheckedIn, refreshSnapshot)
      window.removeEventListener(DISPATCH_EVENT_KEYS.userRefreshCheckedIn, refreshSnapshot)
      window.removeEventListener(DISPATCH_EVENT_KEYS.userCheckedOut, refreshSnapshot)
    }
  }, [])

  useEffect(() => {
    if (!shouldEnableIdleManager && manualIdleTrigger) {
      setManualIdleTrigger(false)
    }
  }, [manualIdleTrigger, shouldEnableIdleManager])

  if (!shouldEnableIdleManager) {
    return null
  }

  return (
    <>
      <CheckInIdlePopup
        key={`${location.pathname}:${snapshot.clinicLocationData?.id || 'emr'}`}
        clinicLocationData={snapshot.clinicLocationData}
        manualTrigger={manualIdleTrigger}
        onManualTrigger={setManualIdleTrigger}
      />
      <LockIconButton onClick={() => setManualIdleTrigger(true)} />
    </>
  )
}
